import type { CanvasElement } from '@/typings/customilyElements/canvasElements';
import type { ListElements } from '@/typings/customilyElements/layer';
import { ListElementType } from '@/typings/customilyElements/listElementType';
import { CanvasEx } from '@/typings/canvas';
import { ElementsGroup } from '@/typings/customilyElements/elementsGroup';
import { ElementsPageEps } from '@/typings/customilyElements/elementsPageEps';

interface RemovedItems {
    groups: ElementsGroup[];
    pageEps: ElementsPageEps[];
    canvasElements: CanvasElement[];
}

const createRemovedItems = (): RemovedItems => ({
    groups: [],
    pageEps: [],
    canvasElements: []
});

const removeRecursively = async (item: ListElements, canvas: CanvasEx, removedItems: RemovedItems) => {
    if (item.type === ListElementType.Group) {
        // Process children first
        if (item.children) {
            for (const child of item.children) {
                await removeRecursively(child, canvas, removedItems);
            }
        }
        removedItems.groups.push(item.element);
        canvas.removeGroup(item.element);
    } else if (item.type === ListElementType.PageEps) {
        // Process children first
        if (item.children) {
            for (const child of item.children) {
                await removeRecursively(child, canvas, removedItems);
            }
        }
        removedItems.pageEps.push(item.element);
        canvas.removePageEps(item.element);
    } else if (
        item.type === ListElementType.CanvasElement &&
        (!item.element.locked || item.element.isSolidBackground)
    ) {
        removedItems.canvasElements.push(item.element);
        canvas.remove(item.element);
    }
    canvas.requestRenderAll();
};

export const removeItemsDeep = async (
    items: ListElements[],
    options: {
        canvas: CanvasEx;
        saveHistory?: boolean;
    }
): Promise<{ type: string; uuid: string; usedAsMask: boolean }[]> => {
    try {
        if (!items || !items.length) return [];

        const { canvas, saveHistory } = options;
        const removedItems = createRemovedItems();

        canvas.discardActiveObject();
        for (const item of items) {
            await removeRecursively(item, canvas, removedItems);
        }

        if (saveHistory) {
            canvas.undoManager?.add(
                (removedItems: RemovedItems) => {
                    canvas.discardActiveObject();
                    if (removedItems.pageEps.length) {
                        removedItems.pageEps.forEach((pageEps) => {
                            canvas.removePageEps.apply(canvas, [pageEps, false]);
                        });
                        canvas.setActiveFirstPageEps.apply(canvas, [false]);
                    }
                    removedItems.groups.forEach((group) => canvas.removeGroup(group));
                    removedItems.canvasElements.forEach((object) => canvas.remove(object));
                },
                [removedItems],
                (removedItems: RemovedItems) => {
                    if (removedItems.pageEps.length) {
                        removedItems.pageEps.forEach((pageEps) => {
                            canvas.addPageEps.apply(canvas, [pageEps]);
                        });
                        const firstPageEps = removedItems.pageEps[0];
                        canvas.addActivePageEps.apply(canvas, [firstPageEps]);
                    }
                    removedItems.groups.forEach((group) => {
                        canvas.addGroup(group, true);
                    });
                    removedItems.canvasElements.forEach((object) => {
                        canvas.add(object);
                        canvas.moveTo(object, object.zIndex);
                    });
                },
                [removedItems]
            );
        }
        canvas.requestRenderAll();

        return removedItems.canvasElements.map((o) => {
            return {
                type: o.type as string,
                uuid: o.uuid as string,
                usedAsMask: o.type === 'bounded-text' && (o.useAsMask || o.useAsCutout)
            };
        });
    } catch (error) {
        console.error('Error removing selected elements:', error);
        return [];
    }
};
